import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";

import { useModalContext } from "@shared/context/ModalContext";
import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import { useUpdateOrganizationUser } from "@shared/queryHooks/useOrganizationUser";

type Props = {
  onCancel: () => void;
};

function WeWorkRemotelyDiscountModal(props: Props) {
  const { onCancel } = props;
  const { removeModal } = useModalContext();
  const { currentOrganization, currentOrganizationUser } = useCurrentSession();
  const { mutate: updateOrganizationUser } = useUpdateOrganizationUser();

  const wwrPercentOff = currentOrganization?.settings?.wwrPercentOff || 0;
  const hasPublishedJobs = currentOrganization?.hasEverPublishedJob || false;
  const isWWRReferral = currentOrganization?.createdVia === "created_via_weworkremotely_referral";
  const isNewWWRUser = isWWRReferral && !hasPublishedJobs;

  const modalTitle = isNewWWRUser ? "Welcome to Polymer" : "You've got a discount";
  const firstParagraph = isNewWWRUser
    ? "Thanks for signing up through We Work Remotely!" :
    <>
      Good news! <span>Your organization has {wwrPercentOff}% off We Work Remotely postings.</span>
    </>
  const secondParagraph = isNewWWRUser
    ? 
    <>
      <span>You'll get {wwrPercentOff}% off all We Work Remotely job postings.</span> After you create a job, check the We Work Remotely section in the <span>Distribution</span> tab to see your discounted pricing.
    </>
    : "After you create a job, check the We Work Remotely section in the Distribution tab to see your discounted pricing.";

  const handleDismiss = () => {
    // Mark modal as displayed
    if (currentOrganizationUser?.id) {
      const updatedSettings = {
        ...currentOrganizationUser.modalDisplaySettings,
        wwrDiscountModalDisplayed: true,
      };

      updateOrganizationUser({
        id: currentOrganizationUser.id,
        modal_display_settings: updatedSettings,
      });
    }

    removeModal();
    onCancel();
  };

  return (
    <CenterModal
      headerTitleText={modalTitle}
      onCancel={handleDismiss} // Make modal dismissible by clicking outside
    >
      <>
        <Styled.Instructions>
          {firstParagraph}
        </Styled.Instructions>
        <Styled.SecondaryMessage>
          {secondParagraph}
        </Styled.SecondaryMessage>
        <Styled.ButtonContainer>
          <Button
            type="button"
            onClick={handleDismiss}
            data-testid="wwr-referral-modal-dismiss-button"
          >
            Got it
          </Button>
        </Styled.ButtonContainer>
      </>
    </CenterModal>
  );
}

export default WeWorkRemotelyDiscountModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Instructions = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: WeWorkRemotelyDiscountModal_Instructions;
    ${[t.mb(3)]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};

    span {
      color: ${t.dark ? t.color.gray[300] : t.color.gray[800]};
      font-weight: 500;
    }
  `;
});

Styled.SecondaryMessage = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: WeWorkRemotelyDiscountModal_SecondaryMessage;
    ${[t.mb(5), t.text.sm]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};

    span {
      color: ${t.dark ? t.color.gray[300] : t.color.gray[800]};
      font-weight: 500;
    }
  `;
});

Styled.ButtonContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WeWorkRemotelyDiscountModal_ButtonContainer;
    display: flex;
  `;
});
